import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Input,
    Textarea,
    Button,
    VStack,
    HStack,
    Text,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuDivider,
    Tooltip,
    Badge,
    Select,
    Switch,
    Collapse,
    Checkbox,
    CheckboxGroup,
    Wrap,
    WrapItem,
} from '@chakra-ui/react';
import {
    DeleteIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ViewIcon,
    SettingsIcon,
} from '@chakra-ui/icons';
import { useToast } from '../hooks/useToast';
import getCSRFtoken from '../utils/getCSRFtoken';
import fetchWithAuth from '../utils/fetchWithAuth';
import { RESPONSE_TEMPLATES } from './ResponseTemplates';
import LoadingSpinner from './shared/LoadingSpinner';

const QUESTION_TYPES = {
    single_choice: { label: 'Single Choice', color: 'gray' },
    multi_select: { label: 'Multi-Select', color: 'blue' },
    matrix: { label: 'Matrix / Grid', color: 'purple' },
    likert: { label: 'Likert Scale', color: 'teal' },
};

/**
 * PollCreator - Streamlined poll creation and editing
 *
 * Combines poll metadata, questions, and choices into one fluid interface.
 * Templates are integrated inline for quick choice population.
 */
function PollCreator() {
    const { pollId } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const csrfToken = getCSRFtoken();

    const isNewPoll = !pollId;

    // Poll state
    const [poll, setPoll] = useState({
        title: '',
        description: '',
        state: 'draft',
    });
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(!isNewPoll);
    const [saving, setSaving] = useState(false);

    // Refs for auto-focus
    const titleRef = useRef(null);

    // Load existing poll data
    useEffect(() => {
        if (pollId) {
            loadPoll();
        } else {
            // Focus title for new polls
            setTimeout(() => titleRef.current?.focus(), 100);
        }
    }, [pollId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadPoll = async () => {
        try {
            setLoading(true);

            // Fetch poll details
            const pollResponse = await fetchWithAuth(`/api/polls/${pollId}/`);
            const pollData = await pollResponse.json();
            setPoll(pollData);

            // Fetch questions
            const questionsResponse = await fetchWithAuth(`/api/questions/?poll=${pollId}`);
            const questionsData = await questionsResponse.json();

            // Ensure choices and matrix_rows arrays exist
            const questionsWithChoices = (questionsData.results || []).map(q => ({
                ...q,
                choices: q.choices || [],
                matrix_rows: q.matrix_rows || [],
            }));
            setQuestions(questionsWithChoices);

        } catch (error) {
            toast.showError('Error', 'Failed to load poll');
            console.error('Load poll error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Save poll (create or update)
    const savePoll = async () => {
        if (!poll.title.trim()) {
            toast.showError('Validation Error', 'Poll title is required');
            titleRef.current?.focus();
            return null;
        }

        setSaving(true);
        try {
            let savedPoll;

            if (isNewPoll) {
                // Create new poll
                const response = await fetchWithAuth('/api/polls/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken,
                    },
                    body: JSON.stringify({
                        title: poll.title,
                        description: poll.description,
                        public_title: poll.public_title || null,
                        public_title_es: poll.public_title_es || null,
                    }),
                });

                if (!response.ok) throw new Error('Failed to create poll');
                savedPoll = await response.json();

                // Navigate to the edit URL
                navigate(`/polls/${savedPoll.id}/edit`, { replace: true });
                toast.showSuccess('Success', 'Poll created');

            } else {
                // Update existing poll
                const response = await fetchWithAuth(`/api/polls/${pollId}/`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken,
                    },
                    body: JSON.stringify({
                        title: poll.title,
                        description: poll.description,
                        public_title: poll.public_title || null,
                        public_title_es: poll.public_title_es || null,
                        completion_question: poll.completion_question || null,
                    }),
                });

                if (!response.ok) throw new Error('Failed to update poll');
                savedPoll = await response.json();
                toast.showSuccess('Success', 'Poll saved');
            }

            setPoll(savedPoll);
            return savedPoll;

        } catch (error) {
            toast.showError('Error', 'Failed to save poll');
            console.error('Save poll error:', error);
            return null;
        } finally {
            setSaving(false);
        }
    };

    // Add a new question
    const addQuestion = async (text = '', questionType = 'single_choice') => {
        // If no poll exists yet, create it first
        let targetPollId = pollId;
        if (isNewPoll) {
            const savedPoll = await savePoll();
            if (!savedPoll) return;
            targetPollId = savedPoll.id;
        }

        if (!text.trim()) {
            text = '';  // Allow empty for immediate editing
        }

        try {
            const response = await fetchWithAuth('/api/questions/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({
                    text: text || 'New question',
                    poll: targetPollId,
                    order: questions.length + 1,
                    question_type: questionType,
                }),
            });

            if (!response.ok) throw new Error('Failed to add question');

            const newQuestion = await response.json();
            newQuestion.choices = newQuestion.choices || [];
            newQuestion.matrix_rows = newQuestion.matrix_rows || [];
            newQuestion.isNew = true;  // Flag to auto-focus

            setQuestions(prev => [...prev, newQuestion]);

        } catch (error) {
            toast.showError('Error', 'Failed to add question');
            console.error('Add question error:', error);
        }
    };

    // Update question text
    const updateQuestion = async (questionId, text) => {
        try {
            const response = await fetchWithAuth(`/api/questions/${questionId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({ text }),
            });

            if (!response.ok) throw new Error('Failed to update question');

            const updated = await response.json();
            setQuestions(prev => prev.map(q =>
                q.id === questionId
                    ? { ...updated, choices: q.choices, matrix_rows: q.matrix_rows }
                    : q
            ));

        } catch (error) {
            toast.showError('Error', 'Failed to update question');
        }
    };

    // Update any field(s) on a question
    const updateQuestionField = async (questionId, fields) => {
        try {
            const response = await fetchWithAuth(`/api/questions/${questionId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify(fields),
            });

            if (!response.ok) throw new Error('Failed to update question');

            const updated = await response.json();
            setQuestions(prev => prev.map(q =>
                q.id === questionId
                    ? { ...updated, choices: q.choices, matrix_rows: q.matrix_rows }
                    : q
            ));

        } catch (error) {
            toast.showError('Error', 'Failed to update question');
        }
    };

    // Delete question
    const deleteQuestion = async (questionId) => {
        try {
            const response = await fetchWithAuth(`/api/questions/${questionId}/`, {
                method: 'DELETE',
                headers: { 'X-CSRFToken': csrfToken },
            });

            if (!response.ok) throw new Error('Failed to delete question');

            setQuestions(prev => prev.filter(q => q.id !== questionId));
            toast.showSuccess('Success', 'Question deleted');

        } catch (error) {
            toast.showError('Error', 'Failed to delete question');
        }
    };

    // Add a choice to a question
    const addChoice = async (questionId, text) => {
        if (!text.trim()) return;

        const question = questions.find(q => q.id === questionId);
        const order = (question?.choices?.length || 0) + 1;

        try {
            const response = await fetchWithAuth('/api/choices/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({
                    text,
                    question: questionId,
                    order,
                }),
            });

            if (!response.ok) throw new Error('Failed to add choice');

            const newChoice = await response.json();

            setQuestions(prev => prev.map(q =>
                q.id === questionId
                    ? { ...q, choices: [...q.choices, newChoice] }
                    : q
            ));

        } catch (error) {
            toast.showError('Error', 'Failed to add choice');
        }
    };

    // Update choice text
    const updateChoice = async (choiceId, text) => {
        try {
            const response = await fetchWithAuth(`/api/choices/${choiceId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({ text }),
            });

            if (!response.ok) throw new Error('Failed to update choice');

            const updated = await response.json();

            setQuestions(prev => prev.map(q => ({
                ...q,
                choices: q.choices.map(c => c.id === choiceId ? updated : c),
            })));

        } catch (error) {
            toast.showError('Error', 'Failed to update choice');
        }
    };

    // Update any field(s) on a choice (e.g. anchor_position)
    const updateChoiceField = async (choiceId, fields) => {
        try {
            const response = await fetchWithAuth(`/api/choices/${choiceId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify(fields),
            });

            if (!response.ok) throw new Error('Failed to update choice');

            const updated = await response.json();

            setQuestions(prev => prev.map(q => ({
                ...q,
                choices: q.choices.map(c => c.id === choiceId ? updated : c),
            })));

        } catch (error) {
            toast.showError('Error', 'Failed to update choice');
        }
    };

    // Delete choice
    const deleteChoice = async (questionId, choiceId) => {
        try {
            const response = await fetchWithAuth(`/api/choices/${choiceId}/`, {
                method: 'DELETE',
                headers: { 'X-CSRFToken': csrfToken },
            });

            if (!response.ok) throw new Error('Failed to delete choice');

            setQuestions(prev => prev.map(q =>
                q.id === questionId
                    ? { ...q, choices: q.choices.filter(c => c.id !== choiceId) }
                    : q
            ));

        } catch (error) {
            toast.showError('Error', 'Failed to delete choice');
        }
    };

    // Matrix row CRUD
    const addMatrixRow = async (questionId, text) => {
        if (!text.trim()) return;

        const question = questions.find(q => q.id === questionId);
        const order = (question?.matrix_rows?.length || 0) + 1;

        try {
            const response = await fetchWithAuth('/api/matrix-rows/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({ text, question: questionId, order }),
            });

            if (!response.ok) throw new Error('Failed to add matrix row');

            const newRow = await response.json();

            setQuestions(prev => prev.map(q =>
                q.id === questionId
                    ? { ...q, matrix_rows: [...(q.matrix_rows || []), newRow] }
                    : q
            ));

        } catch (error) {
            toast.showError('Error', 'Failed to add row');
        }
    };

    const updateMatrixRow = async (rowId, text) => {
        try {
            const response = await fetchWithAuth(`/api/matrix-rows/${rowId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({ text }),
            });

            if (!response.ok) throw new Error('Failed to update matrix row');

            const updated = await response.json();

            setQuestions(prev => prev.map(q => ({
                ...q,
                matrix_rows: (q.matrix_rows || []).map(r => r.id === rowId ? updated : r),
            })));

        } catch (error) {
            toast.showError('Error', 'Failed to update row');
        }
    };

    const updateMatrixRowField = async (rowId, fields) => {
        try {
            const response = await fetchWithAuth(`/api/matrix-rows/${rowId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify(fields),
            });

            if (!response.ok) throw new Error('Failed to update matrix row');

            const updated = await response.json();

            setQuestions(prev => prev.map(q => ({
                ...q,
                matrix_rows: (q.matrix_rows || []).map(r => r.id === rowId ? updated : r),
            })));

        } catch (error) {
            toast.showError('Error', 'Failed to update row');
        }
    };

    const deleteMatrixRow = async (questionId, rowId) => {
        try {
            const response = await fetchWithAuth(`/api/matrix-rows/${rowId}/`, {
                method: 'DELETE',
                headers: { 'X-CSRFToken': csrfToken },
            });

            if (!response.ok) throw new Error('Failed to delete matrix row');

            setQuestions(prev => prev.map(q =>
                q.id === questionId
                    ? { ...q, matrix_rows: (q.matrix_rows || []).filter(r => r.id !== rowId) }
                    : q
            ));

        } catch (error) {
            toast.showError('Error', 'Failed to delete row');
        }
    };

    // Apply template to a question
    const applyTemplate = async (questionId, templateKey) => {
        const template = RESPONSE_TEMPLATES[templateKey];
        if (!template) return;

        const question = questions.find(q => q.id === questionId);
        const startingOrder = question?.choices?.length || 0;

        try {
            const newChoices = [];

            for (let i = 0; i < template.choices.length; i++) {
                const response = await fetchWithAuth('/api/choices/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken,
                    },
                    body: JSON.stringify({
                        text: template.choices[i],
                        question: questionId,
                        order: startingOrder + i + 1,
                    }),
                });

                if (response.ok) {
                    const newChoice = await response.json();
                    newChoices.push(newChoice);
                }
            }

            setQuestions(prev => prev.map(q =>
                q.id === questionId
                    ? { ...q, choices: [...q.choices, ...newChoices] }
                    : q
            ));

            toast.showSuccess('Success', `Added ${template.name}`);

        } catch (error) {
            toast.showError('Error', 'Failed to apply template');
        }
    };

    // Move question up/down
    const moveQuestion = async (questionId, direction) => {
        const currentIndex = questions.findIndex(q => q.id === questionId);
        if (
            (direction === 'up' && currentIndex === 0) ||
            (direction === 'down' && currentIndex === questions.length - 1)
        ) {
            return;
        }

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        const reordered = [...questions];
        const [moved] = reordered.splice(currentIndex, 1);
        reordered.splice(newIndex, 0, moved);

        // Optimistic update
        setQuestions(reordered);

        // Update order on backend
        try {
            for (let i = 0; i < reordered.length; i++) {
                await fetchWithAuth(`/api/questions/${reordered[i].id}/`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken,
                    },
                    body: JSON.stringify({ order: i + 1 }),
                });
            }
        } catch (error) {
            toast.showError('Error', 'Failed to reorder questions');
            loadPoll();  // Reload to get correct order
        }
    };

    // Move choice up/down within a question
    const moveChoice = async (questionId, choiceId, direction) => {
        const question = questions.find(q => q.id === questionId);
        if (!question) return;

        const choices = [...question.choices];
        const currentIndex = choices.findIndex(c => c.id === choiceId);
        if (
            (direction === 'up' && currentIndex === 0) ||
            (direction === 'down' && currentIndex === choices.length - 1)
        ) {
            return;
        }

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        const [moved] = choices.splice(currentIndex, 1);
        choices.splice(newIndex, 0, moved);

        // Optimistic update
        setQuestions(prev => prev.map(q =>
            q.id === questionId ? { ...q, choices } : q
        ));

        // Update order on backend
        try {
            await Promise.all(choices.map((c, i) =>
                fetchWithAuth(`/api/choices/${c.id}/`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken,
                    },
                    body: JSON.stringify({ order: i + 1 }),
                })
            ));
        } catch (error) {
            toast.showError('Error', 'Failed to reorder choices');
            loadPoll();
        }
    };

    // Preview survey
    const openPreview = () => {
        if (pollId) {
            window.open(`/preview-poll/${pollId}`, '_blank', 'width=500,height=900,scrollbars=yes');
        }
    };

    // Go to poll settings
    const goToSettings = () => {
        if (pollId) {
            navigate(`/polls/${pollId}/settings`);
        }
    };

    if (loading) {
        return (
            <Box minH="100vh" bg="background.shade2" display="flex" alignItems="center" justifyContent="center">
                <LoadingSpinner />
            </Box>
        );
    }

    return (
        <Box minH="100vh" bg="background.shade2" py={8}>
            <Box maxW="700px" mx="auto" px={4}>
                {/* Header Actions */}
                <HStack justify="space-between" mb={6}>
                    <Text fontSize="sm" color="gray.500">
                        {isNewPoll ? 'New Poll' : (
                            <HStack spacing={2}>
                                <Text>Editing</Text>
                                <Badge colorScheme={poll.state === 'draft' ? 'gray' : poll.state === 'live' ? 'green' : 'blue'}>
                                    {poll.state}
                                </Badge>
                            </HStack>
                        )}
                    </Text>
                    <HStack spacing={2}>
                        {!isNewPoll && questions.length > 0 && (
                            <Tooltip label="Preview Survey">
                                <Button
                                    leftIcon={<ViewIcon />}
                                    size="sm"
                                    variant="outline"
                                    onClick={openPreview}
                                >
                                    Preview
                                </Button>
                            </Tooltip>
                        )}
                        {!isNewPoll && (
                            <Tooltip label="Poll Settings (Stage, Go Live, etc.)">
                                <Button
                                    leftIcon={<SettingsIcon />}
                                    size="sm"
                                    variant="outline"
                                    onClick={goToSettings}
                                >
                                    Settings
                                </Button>
                            </Tooltip>
                        )}
                        <Button
                            colorScheme="teal"
                            size="sm"
                            onClick={savePoll}
                            isLoading={saving}
                            loadingText="Saving..."
                        >
                            {isNewPoll ? 'Create Poll' : 'Save'}
                        </Button>
                    </HStack>
                </HStack>

                {/* Poll Title & Description */}
                <Box bg="white" borderRadius="md" border="1px solid" borderColor="border.default" p={6} mb={6}>
                    <VStack align="stretch" spacing={4}>
                        <Input
                            ref={titleRef}
                            value={poll.title}
                            onChange={(e) => {
                                setPoll(p => ({ ...p, title: e.target.value }));
                                                            }}
                            placeholder="Poll title"
                            fontSize="2xl"
                            fontWeight="bold"
                            variant="unstyled"
                            _placeholder={{ color: 'gray.400' }}
                        />
                        <Textarea
                            value={poll.description || ''}
                            onChange={(e) => {
                                setPoll(p => ({ ...p, description: e.target.value }));
                                                            }}
                            placeholder="Description (optional)"
                            variant="unstyled"
                            resize="none"
                            rows={2}
                            _placeholder={{ color: 'gray.400' }}
                        />
                        <Input
                            value={poll.public_title || ''}
                            onChange={(e) => {
                                setPoll(p => ({ ...p, public_title: e.target.value }));
                            }}
                            placeholder="Public-facing title (shown to respondents, e.g. 'Raise Your Voice')"
                            size="sm"
                            _placeholder={{ color: 'gray.400' }}
                        />
                        <Input
                            value={poll.public_title_es || ''}
                            onChange={(e) => {
                                setPoll(p => ({ ...p, public_title_es: e.target.value }));
                            }}
                            placeholder="Public-facing title - Spanish (optional)"
                            size="sm"
                            _placeholder={{ color: 'gray.400' }}
                        />
                        {/* Completion question selector */}
                        {!isNewPoll && questions.length > 0 && (
                            <Box>
                                <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>
                                    Completion point
                                </Text>
                                <Select
                                    size="sm"
                                    value={poll.completion_question || ''}
                                    onChange={(e) => {
                                        const val = e.target.value ? parseInt(e.target.value) : null;
                                        setPoll(p => ({ ...p, completion_question: val }));
                                    }}
                                    placeholder="Last question (default)"
                                >
                                    {questions.map(q => (
                                        <option key={q.id} value={q.id}>
                                            {q.text?.substring(0, 80) || `Question ${q.id}`}
                                        </option>
                                    ))}
                                </Select>
                                <Text fontSize="xs" color="gray.400" mt={1}>
                                    Respondent is marked complete after answering this question. Everything after is optional.
                                </Text>
                            </Box>
                        )}
                    </VStack>
                </Box>

                {/* Questions */}
                <VStack align="stretch" spacing={4}>
                    {questions.map((question, index) => (
                        <QuestionCard
                            key={question.id}
                            question={question}
                            index={index}
                            totalQuestions={questions.length}
                            allQuestions={questions}
                            onUpdateQuestion={updateQuestion}
                            onUpdateQuestionField={updateQuestionField}
                            onDeleteQuestion={deleteQuestion}
                            onAddChoice={addChoice}
                            onUpdateChoice={updateChoice}
                            onDeleteChoice={deleteChoice}
                            onUpdateChoiceField={updateChoiceField}
                            onApplyTemplate={applyTemplate}
                            onMoveQuestion={moveQuestion}
                            onMoveChoice={moveChoice}
                            onAddMatrixRow={addMatrixRow}
                            onUpdateMatrixRow={updateMatrixRow}
                            onUpdateMatrixRowField={updateMatrixRowField}
                            onDeleteMatrixRow={deleteMatrixRow}
                        />
                    ))}

                    {/* Add Question Menu */}
                    <Menu>
                        <MenuButton
                            as={Button}
                            variant="outline"
                            borderStyle="dashed"
                            borderColor="gray.300"
                            color="gray.500"
                            py={8}
                            w="100%"
                            _hover={{ borderColor: 'teal.500', color: 'teal.500', bg: 'teal.50' }}
                        >
                            + Add Question
                        </MenuButton>
                        <MenuList>
                            {Object.entries(QUESTION_TYPES).map(([type, { label }]) => (
                                <MenuItem key={type} onClick={() => addQuestion('', type)}>
                                    {label}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Menu>
                </VStack>
            </Box>
        </Box>
    );
}

/**
 * QuestionCard - Individual question with inline editing and type configuration
 */
function QuestionCard({
    question,
    index,
    totalQuestions,
    allQuestions,
    onUpdateQuestion,
    onUpdateQuestionField,
    onDeleteQuestion,
    onAddChoice,
    onUpdateChoice,
    onDeleteChoice,
    onUpdateChoiceField,
    onApplyTemplate,
    onMoveQuestion,
    onMoveChoice,
    onAddMatrixRow,
    onUpdateMatrixRow,
    onUpdateMatrixRowField,
    onDeleteMatrixRow,
}) {
    const [questionText, setQuestionText] = useState(question.text);
    const [questionTextEs, setQuestionTextEs] = useState(question.text_es || '');
    const [newChoiceText, setNewChoiceText] = useState('');
    const [newMatrixRowText, setNewMatrixRowText] = useState('');
    const [maxSelections, setMaxSelections] = useState(question.max_selections || '');
    const [rotationGroup, setRotationGroup] = useState(question.rotation_group || '');
    const [isHovered, setIsHovered] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const questionInputRef = useRef(null);

    const questionType = question.question_type || 'single_choice';
    const typeInfo = QUESTION_TYPES[questionType] || QUESTION_TYPES.single_choice;

    // Sync local state when question props change
    useEffect(() => {
        setMaxSelections(question.max_selections || '');
    }, [question.max_selections]);

    useEffect(() => {
        setRotationGroup(question.rotation_group || '');
    }, [question.rotation_group]);

    // Auto-focus new questions
    useEffect(() => {
        if (question.isNew) {
            questionInputRef.current?.focus();
            questionInputRef.current?.select();
        }
    }, [question.isNew]);

    // Save question on blur
    const handleQuestionBlur = () => {
        if (questionText !== question.text && questionText.trim()) {
            onUpdateQuestion(question.id, questionText);
        }
    };

    const handleQuestionEsBlur = () => {
        if (questionTextEs !== (question.text_es || '')) {
            onUpdateQuestionField(question.id, { text_es: questionTextEs || null });
        }
    };

    // Add choice on Enter
    const handleChoiceKeyDown = (e) => {
        if (e.key === 'Enter' && newChoiceText.trim()) {
            onAddChoice(question.id, newChoiceText);
            setNewChoiceText('');
        }
    };

    // Save max_selections on blur
    const handleMaxSelectionsBlur = () => {
        const val = maxSelections ? parseInt(maxSelections) : null;
        if (val !== question.max_selections) {
            onUpdateQuestionField(question.id, { max_selections: val });
        }
    };

    // Save rotation_group on blur
    const handleRotationGroupBlur = () => {
        const val = rotationGroup.trim() || null;
        if (val !== question.rotation_group) {
            onUpdateQuestionField(question.id, { rotation_group: val });
        }
    };

    // Template categories for menu
    const templateCategories = [...new Set(Object.values(RESPONSE_TEMPLATES).map(t => t.category))];

    // Choice indicator symbol
    const choiceIndicator = questionType === 'multi_select' ? '\u2610' : '\u25CB';

    return (
        <Box
            bg="white"
            borderRadius="md"
            border="1px solid"
            borderColor="border.default"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Question Header */}
            <HStack p={4} spacing={3} borderBottom="1px solid" borderColor="border.light">
                <Text color="gray.400" fontSize="sm" fontWeight="medium" minW="24px">
                    {index + 1}.
                </Text>
                <VStack spacing={1} flex={1} align="stretch">
                    <Input
                        ref={questionInputRef}
                        value={questionText}
                        onChange={(e) => setQuestionText(e.target.value)}
                        onBlur={handleQuestionBlur}
                        placeholder="Enter your question..."
                        variant="unstyled"
                        fontWeight="medium"
                    />
                    <Input
                        value={questionTextEs}
                        onChange={(e) => setQuestionTextEs(e.target.value)}
                        onBlur={handleQuestionEsBlur}
                        placeholder="Pregunta en espanol..."
                        variant="unstyled"
                        fontSize="sm"
                        color="gray.500"
                    />
                </VStack>
                <Badge colorScheme={typeInfo.color} fontSize="xs">
                    {typeInfo.label}
                </Badge>

                {/* Settings, Reorder & Delete - show on hover */}
                <HStack spacing={1} opacity={isHovered ? 1 : 0} transition="opacity 0.2s">
                    <Tooltip label="Question settings">
                        <IconButton
                            icon={<SettingsIcon />}
                            size="sm"
                            variant="ghost"
                            aria-label="Question settings"
                            onClick={() => setShowSettings(!showSettings)}
                            color={showSettings ? 'teal.500' : undefined}
                        />
                    </Tooltip>
                    <IconButton
                        icon={<ChevronUpIcon />}
                        size="sm"
                        variant="ghost"
                        aria-label="Move up"
                        isDisabled={index === 0}
                        onClick={() => onMoveQuestion(question.id, 'up')}
                    />
                    <IconButton
                        icon={<ChevronDownIcon />}
                        size="sm"
                        variant="ghost"
                        aria-label="Move down"
                        isDisabled={index === totalQuestions - 1}
                        onClick={() => onMoveQuestion(question.id, 'down')}
                    />
                    <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        aria-label="Delete question"
                        onClick={() => onDeleteQuestion(question.id)}
                    />
                </HStack>
            </HStack>

            {/* Question Type & Inline Settings */}
            <HStack
                p={3}
                px={4}
                spacing={4}
                borderBottom="1px solid"
                borderColor="border.light"
                bg="gray.50"
                flexWrap="wrap"
            >
                <HStack spacing={2}>
                    <Text fontSize="xs" color="gray.500" fontWeight="medium">Type:</Text>
                    <Select
                        size="xs"
                        w="150px"
                        value={questionType}
                        onChange={(e) => onUpdateQuestionField(question.id, { question_type: e.target.value })}
                        bg="white"
                    >
                        {Object.entries(QUESTION_TYPES).map(([type, { label }]) => (
                            <option key={type} value={type}>{label}</option>
                        ))}
                    </Select>
                </HStack>

                {questionType === 'multi_select' && (
                    <HStack spacing={2}>
                        <Text fontSize="xs" color="gray.500" fontWeight="medium" whiteSpace="nowrap">
                            Max selections:
                        </Text>
                        <Input
                            size="xs"
                            w="55px"
                            type="number"
                            min={1}
                            value={maxSelections}
                            placeholder="--"
                            bg="white"
                            onChange={(e) => setMaxSelections(e.target.value)}
                            onBlur={handleMaxSelectionsBlur}
                        />
                    </HStack>
                )}

                <HStack spacing={2} ml="auto">
                    <Text fontSize="xs" color="gray.500">Randomize</Text>
                    <Switch
                        size="sm"
                        isChecked={question.randomize_choices || false}
                        onChange={(e) => onUpdateQuestionField(question.id, { randomize_choices: e.target.checked })}
                        colorScheme="teal"
                    />
                </HStack>
            </HStack>

            {/* Advanced Settings (collapsible) */}
            <Collapse in={showSettings} animateOpacity>
                <Box p={4} bg="gray.50" borderBottom="1px solid" borderColor="border.light">
                    <VStack align="stretch" spacing={4}>
                        {/* Rotation Group */}
                        <HStack spacing={4} flexWrap="wrap">
                            <HStack spacing={2} flex={1} minW="200px">
                                <Text fontSize="xs" color="gray.500" fontWeight="medium" whiteSpace="nowrap">
                                    Rotation group:
                                </Text>
                                <Input
                                    size="xs"
                                    placeholder="e.g. ballot_candidates"
                                    value={rotationGroup}
                                    onChange={(e) => setRotationGroup(e.target.value)}
                                    onBlur={handleRotationGroupBlur}
                                    bg="white"
                                    flex={1}
                                />
                            </HStack>
                        </HStack>
                        <Text fontSize="xs" color="gray.400" mt={-2}>
                            Questions in the same rotation group share their randomized choice order.
                        </Text>

                        {/* Skip Logic */}
                        <SkipLogicEditor
                            question={question}
                            allQuestions={allQuestions}
                            onUpdateQuestionField={onUpdateQuestionField}
                        />
                    </VStack>
                </Box>
            </Collapse>

            {/* Matrix Rows (for matrix type) */}
            {questionType === 'matrix' && (
                <Box p={4} borderBottom="1px solid" borderColor="border.light">
                    <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={2} letterSpacing="wide">
                        ROW ENTITIES (candidates, issues, etc.)
                    </Text>
                    <VStack align="stretch" spacing={2}>
                        {(question.matrix_rows || []).map((row) => (
                            <MatrixRowEditor
                                key={row.id}
                                row={row}
                                questionId={question.id}
                                onUpdateMatrixRow={onUpdateMatrixRow}
                                onUpdateMatrixRowField={onUpdateMatrixRowField}
                                onDeleteMatrixRow={onDeleteMatrixRow}
                            />
                        ))}

                        {/* Add Matrix Row */}
                        <HStack spacing={2}>
                            <Box w="20px" />
                            <Input
                                value={newMatrixRowText}
                                onChange={(e) => setNewMatrixRowText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && newMatrixRowText.trim()) {
                                        onAddMatrixRow(question.id, newMatrixRowText);
                                        setNewMatrixRowText('');
                                    }
                                }}
                                placeholder="Add row entity (e.g. Candidate Name)..."
                                size="sm"
                                bg="white"
                                flex={1}
                            />
                            <Button
                                size="sm"
                                colorScheme="purple"
                                variant="ghost"
                                onClick={() => {
                                    if (newMatrixRowText.trim()) {
                                        onAddMatrixRow(question.id, newMatrixRowText);
                                        setNewMatrixRowText('');
                                    }
                                }}
                                isDisabled={!newMatrixRowText.trim()}
                            >
                                Add
                            </Button>
                        </HStack>
                    </VStack>
                </Box>
            )}

            {/* Choices */}
            <Box p={4} bg="background.shade1">
                {questionType === 'matrix' && (
                    <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={2} letterSpacing="wide">
                        SCALE POINTS (columns)
                    </Text>
                )}
                <VStack align="stretch" spacing={2}>
                    {question.choices.map((choice, choiceIndex) => (
                        <ChoiceRow
                            key={choice.id}
                            choice={choice}
                            choiceIndex={choiceIndex}
                            totalChoices={question.choices.length}
                            questionId={question.id}
                            indicator={choiceIndicator}
                            onUpdateChoice={onUpdateChoice}
                            onDeleteChoice={onDeleteChoice}
                            onUpdateChoiceField={onUpdateChoiceField}
                            onMoveChoice={onMoveChoice}
                        />
                    ))}

                    {/* Add Choice Input */}
                    <HStack spacing={2}>
                        <Box w="20px" /> {/* Spacer for alignment */}
                        <Input
                            value={newChoiceText}
                            onChange={(e) => setNewChoiceText(e.target.value)}
                            onKeyDown={handleChoiceKeyDown}
                            placeholder={questionType === 'matrix' ? 'Add a scale point...' : 'Add a choice...'}
                            size="sm"
                            bg="white"
                            flex={1}
                        />
                        <Button
                            size="sm"
                            colorScheme="teal"
                            variant="ghost"
                            onClick={() => {
                                if (newChoiceText.trim()) {
                                    onAddChoice(question.id, newChoiceText);
                                    setNewChoiceText('');
                                }
                            }}
                            isDisabled={!newChoiceText.trim()}
                        >
                            Add
                        </Button>
                    </HStack>

                    {/* Template Menu */}
                    {question.choices.length === 0 && (
                        <HStack spacing={2} pt={2}>
                            <Text fontSize="xs" color="gray.500">Quick add:</Text>
                            <Button
                                size="xs"
                                variant="outline"
                                onClick={() => onApplyTemplate(question.id, 'likert5')}
                            >
                                Likert
                            </Button>
                            <Button
                                size="xs"
                                variant="outline"
                                onClick={() => onApplyTemplate(question.id, 'yesNo')}
                            >
                                Yes/No
                            </Button>
                            <Menu>
                                <MenuButton as={Button} size="xs" variant="outline" rightIcon={<ChevronDownIcon />}>
                                    More
                                </MenuButton>
                                <MenuList fontSize="sm">
                                    {templateCategories.map(category => (
                                        <React.Fragment key={category}>
                                            <Text px={3} py={1} fontSize="xs" fontWeight="bold" color="gray.500">
                                                {category}
                                            </Text>
                                            {Object.entries(RESPONSE_TEMPLATES)
                                                .filter(([_, t]) => t.category === category)
                                                .map(([key, template]) => (
                                                    <MenuItem
                                                        key={key}
                                                        onClick={() => onApplyTemplate(question.id, key)}
                                                    >
                                                        {template.name}
                                                    </MenuItem>
                                                ))
                                            }
                                            <MenuDivider />
                                        </React.Fragment>
                                    ))}
                                </MenuList>
                            </Menu>
                        </HStack>
                    )}

                    {/* Template dropdown when choices exist */}
                    {question.choices.length > 0 && (
                        <HStack justify="flex-end" pt={1}>
                            <Menu>
                                <MenuButton
                                    as={Button}
                                    size="xs"
                                    variant="ghost"
                                    rightIcon={<ChevronDownIcon />}
                                    color="gray.500"
                                >
                                    Add from template
                                </MenuButton>
                                <MenuList fontSize="sm">
                                    {templateCategories.map(category => (
                                        <React.Fragment key={category}>
                                            <Text px={3} py={1} fontSize="xs" fontWeight="bold" color="gray.500">
                                                {category}
                                            </Text>
                                            {Object.entries(RESPONSE_TEMPLATES)
                                                .filter(([_, t]) => t.category === category)
                                                .map(([key, template]) => (
                                                    <MenuItem
                                                        key={key}
                                                        onClick={() => onApplyTemplate(question.id, key)}
                                                    >
                                                        {template.name}
                                                    </MenuItem>
                                                ))
                                            }
                                            <MenuDivider />
                                        </React.Fragment>
                                    ))}
                                </MenuList>
                            </Menu>
                        </HStack>
                    )}
                </VStack>
            </Box>
        </Box>
    );
}

/**
 * MatrixRowEditor - Inline editor for a single matrix row entity
 */
function MatrixRowEditor({ row, questionId, onUpdateMatrixRow, onUpdateMatrixRowField, onDeleteMatrixRow }) {
    const [text, setText] = useState(row.text);
    const [textEs, setTextEs] = useState(row.text_es || '');
    const [isHovered, setIsHovered] = useState(false);

    const handleBlur = () => {
        if (text !== row.text && text.trim()) {
            onUpdateMatrixRow(row.id, text);
        }
    };

    const handleEsBlur = () => {
        if (textEs !== (row.text_es || '')) {
            onUpdateMatrixRowField(row.id, { text_es: textEs || null });
        }
    };

    return (
        <HStack
            spacing={2}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Box w="20px" textAlign="center">
                <Text color="purple.400" fontSize="sm">&bull;</Text>
            </Box>
            <VStack flex={1} spacing={1} align="stretch">
                <Input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onBlur={handleBlur}
                    size="sm"
                    bg="white"
                    border="1px solid"
                    borderColor="purple.200"
                    _hover={{ borderColor: 'purple.300' }}
                />
                <Input
                    value={textEs}
                    onChange={(e) => setTextEs(e.target.value)}
                    onBlur={handleEsBlur}
                    size="xs"
                    bg="white"
                    border="1px solid"
                    borderColor="purple.100"
                    color="gray.500"
                    placeholder="Español..."
                    _hover={{ borderColor: 'purple.200' }}
                />
            </VStack>
            <IconButton
                icon={<DeleteIcon />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                aria-label="Delete row"
                opacity={isHovered ? 1 : 0}
                transition="opacity 0.2s"
                onClick={() => onDeleteMatrixRow(questionId, row.id)}
            />
        </HStack>
    );
}

/**
 * SkipLogicEditor - Configure conditional display logic for a question
 */
function SkipLogicEditor({ question, allQuestions, onUpdateQuestionField }) {
    const skipLogic = question.skip_logic || null;
    const hasSkipLogic = skipLogic && skipLogic.show_if;

    // Parse existing skip logic into local state
    const existing = hasSkipLogic ? skipLogic.show_if : {};
    const [enabled, setEnabled] = useState(hasSkipLogic);
    const [sourceQuestionId, setSourceQuestionId] = useState(
        existing.question_id ? String(existing.question_id) : ''
    );
    const [operator, setOperator] = useState(existing.operator || 'includes_any');
    const [selectedChoiceIds, setSelectedChoiceIds] = useState(
        (existing.choice_ids || []).map(String)
    );

    // Questions that come before this one (can't depend on later questions)
    const priorQuestions = allQuestions.filter(q => {
        const qOrder = q.order || 0;
        const thisOrder = question.order || 0;
        return q.id !== question.id && qOrder < thisOrder;
    });

    // Get choices for the selected source question
    const sourceQuestion = allQuestions.find(q => String(q.id) === sourceQuestionId);
    const sourceChoices = sourceQuestion?.choices || [];

    // Save skip logic to backend
    const saveSkipLogic = (newEnabled, newSourceId, newOperator, newChoiceIds) => {
        if (!newEnabled || !newSourceId) {
            onUpdateQuestionField(question.id, { skip_logic: null });
            return;
        }

        const needsChoices = newOperator !== 'answered' && newOperator !== 'not_answered';
        const choiceIds = needsChoices ? newChoiceIds.map(Number).filter(Boolean) : [];

        if (needsChoices && choiceIds.length === 0) return;

        const logic = {
            show_if: {
                question_id: parseInt(newSourceId),
                operator: newOperator,
                choice_ids: choiceIds,
            }
        };
        onUpdateQuestionField(question.id, { skip_logic: logic });
    };

    const handleToggle = (e) => {
        const newEnabled = e.target.checked;
        setEnabled(newEnabled);
        if (!newEnabled) {
            saveSkipLogic(false, null, null, []);
        }
    };

    const handleSourceChange = (e) => {
        const newSourceId = e.target.value;
        setSourceQuestionId(newSourceId);
        setSelectedChoiceIds([]);
        // Don't save yet - wait for choices to be selected
    };

    const handleOperatorChange = (e) => {
        const newOp = e.target.value;
        setOperator(newOp);
        if (newOp === 'answered' || newOp === 'not_answered') {
            saveSkipLogic(true, sourceQuestionId, newOp, []);
        } else if (selectedChoiceIds.length > 0) {
            saveSkipLogic(true, sourceQuestionId, newOp, selectedChoiceIds);
        }
    };

    const handleChoiceToggle = (choiceId) => {
        const newIds = selectedChoiceIds.includes(choiceId)
            ? selectedChoiceIds.filter(id => id !== choiceId)
            : [...selectedChoiceIds, choiceId];
        setSelectedChoiceIds(newIds);
        saveSkipLogic(true, sourceQuestionId, operator, newIds);
    };

    const needsChoices = operator !== 'answered' && operator !== 'not_answered';

    return (
        <Box>
            <HStack spacing={2} mb={enabled ? 3 : 0}>
                <Text fontSize="xs" color="gray.500" fontWeight="medium">Skip logic:</Text>
                <Switch
                    size="sm"
                    isChecked={enabled}
                    onChange={handleToggle}
                    colorScheme="teal"
                />
                <Text fontSize="xs" color="gray.400">
                    {enabled ? 'Show this question conditionally' : 'Always show'}
                </Text>
            </HStack>

            {enabled && (
                <VStack align="stretch" spacing={2} pl={0}>
                    <HStack spacing={2} flexWrap="wrap">
                        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">Show if Q</Text>
                        <Select
                            size="xs"
                            w="220px"
                            value={sourceQuestionId}
                            onChange={handleSourceChange}
                            bg="white"
                            placeholder="Select question..."
                        >
                            {priorQuestions.map(q => (
                                <option key={q.id} value={q.id}>
                                    {q.text ? q.text.substring(0, 40) : `Question ${q.id}`}
                                    {q.text && q.text.length > 40 ? '...' : ''}
                                </option>
                            ))}
                        </Select>
                        <Select
                            size="xs"
                            w="140px"
                            value={operator}
                            onChange={handleOperatorChange}
                            bg="white"
                        >
                            <option value="includes_any">includes any of</option>
                            <option value="excludes_all">excludes all of</option>
                            <option value="answered">was answered</option>
                            <option value="not_answered">was not answered</option>
                        </Select>
                    </HStack>

                    {needsChoices && sourceQuestion && sourceChoices.length > 0 && (
                        <Wrap spacing={2} pl={0}>
                            {sourceChoices.map(c => (
                                <WrapItem key={c.id}>
                                    <Checkbox
                                        size="sm"
                                        isChecked={selectedChoiceIds.includes(String(c.id))}
                                        onChange={() => handleChoiceToggle(String(c.id))}
                                        colorScheme="teal"
                                    >
                                        <Text fontSize="xs">{c.text}</Text>
                                    </Checkbox>
                                </WrapItem>
                            ))}
                        </Wrap>
                    )}

                    {needsChoices && sourceQuestionId && sourceChoices.length === 0 && (
                        <Text fontSize="xs" color="gray.400">
                            Selected question has no choices yet.
                        </Text>
                    )}
                </VStack>
            )}
        </Box>
    );
}

/**
 * ChoiceRow - Individual choice with inline editing and anchor position
 */
function ChoiceRow({ choice, choiceIndex, totalChoices, questionId, indicator, onUpdateChoice, onDeleteChoice, onUpdateChoiceField, onMoveChoice }) {
    const [text, setText] = useState(choice.text);
    const [textEs, setTextEs] = useState(choice.text_es || '');
    const [isHovered, setIsHovered] = useState(false);

    const handleBlur = () => {
        if (text !== choice.text && text.trim()) {
            onUpdateChoice(choice.id, text);
        }
    };

    const handleEsBlur = () => {
        if (textEs !== (choice.text_es || '')) {
            onUpdateChoiceField(choice.id, { text_es: textEs || null });
        }
    };

    const anchorPosition = choice.anchor_position || 'none';

    return (
        <HStack
            spacing={2}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Box w="20px" textAlign="center">
                <Text color="gray.400" fontSize="sm">{indicator}</Text>
            </Box>
            <VStack spacing={0} flex={1} align="stretch">
                <Input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onBlur={handleBlur}
                    size="sm"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    _hover={{ borderColor: 'gray.300' }}
                />
                <Input
                    value={textEs}
                    onChange={(e) => setTextEs(e.target.value)}
                    onBlur={handleEsBlur}
                    size="xs"
                    variant="unstyled"
                    color="gray.400"
                    placeholder="Espanol..."
                    px={3}
                    py={1}
                />
            </VStack>

            {/* Anchor position control */}
            {isHovered && (
                <Menu>
                    <MenuButton
                        as={Button}
                        size="xs"
                        variant={anchorPosition !== 'none' ? 'solid' : 'ghost'}
                        colorScheme={anchorPosition !== 'none' ? 'orange' : 'gray'}
                        fontSize="10px"
                        minW="auto"
                        px={2}
                    >
                        {anchorPosition === 'top' ? 'Top' : anchorPosition === 'bottom' ? 'Bottom' : 'Pin'}
                    </MenuButton>
                    <MenuList fontSize="sm" minW="120px">
                        <MenuItem onClick={() => onUpdateChoiceField(choice.id, { anchor_position: 'none' })}>
                            No anchor
                        </MenuItem>
                        <MenuItem onClick={() => onUpdateChoiceField(choice.id, { anchor_position: 'top' })}>
                            Pin to top
                        </MenuItem>
                        <MenuItem onClick={() => onUpdateChoiceField(choice.id, { anchor_position: 'bottom' })}>
                            Pin to bottom
                        </MenuItem>
                    </MenuList>
                </Menu>
            )}

            {/* Static badge when anchored and not hovered */}
            {!isHovered && anchorPosition !== 'none' && (
                <Badge colorScheme="orange" fontSize="10px">
                    {anchorPosition === 'top' ? 'Top' : 'Bottom'}
                </Badge>
            )}

            <HStack spacing={0} opacity={isHovered ? 1 : 0} transition="opacity 0.2s">
                <IconButton
                    icon={<ChevronUpIcon />}
                    size="xs"
                    variant="ghost"
                    aria-label="Move choice up"
                    isDisabled={choiceIndex === 0}
                    onClick={() => onMoveChoice(questionId, choice.id, 'up')}
                />
                <IconButton
                    icon={<ChevronDownIcon />}
                    size="xs"
                    variant="ghost"
                    aria-label="Move choice down"
                    isDisabled={choiceIndex === totalChoices - 1}
                    onClick={() => onMoveChoice(questionId, choice.id, 'down')}
                />
                <IconButton
                    icon={<DeleteIcon />}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    aria-label="Delete choice"
                    onClick={() => onDeleteChoice(questionId, choice.id)}
                />
            </HStack>
        </HStack>
    );
}

export default PollCreator;
